import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';

import { RouterModule, Router, NavigationEnd } from '@angular/router';

import { SynergyComponentsModule, SynSideNavComponent } from '@synergy-design-system/angular';
import { filter } from 'rxjs';
import { pageUrls } from 'src/app/configuration/pages';
import { LocalizationService } from 'src/app/internationalization/localization.service';
import { RbacService } from '@core/services/rbac.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

export interface NavItem {
  label: string;
  route: string;
  icon: string;
  divider?: boolean;
  footer?: boolean;
  /** RBAC gate: subject + method required to show this item. Omit = always visible. */
  requiredPermission?: { subject: string; method: string };
}

export const NAVIGATION_CONFIG: NavItem[] = [
  {
    label: 'navigation.dashboard',
    route: pageUrls.dashboard,
    icon: 'space_dashboard_fill',
    footer: false,
  },
  {
    label: 'navigation.scanHistoryManagement',
    route: pageUrls.scanHistories,
    icon: 'history_fill',
    divider: true,
    footer: false,
    requiredPermission: { subject: 'scan-histories', method: 'GET' },
  },
  {
    label: 'navigation.assetManagement',
    route: pageUrls.assets,
    icon: 'inventory_2_fill',
    divider: true,
    footer: false,
    requiredPermission: { subject: 'assets', method: 'GET' },
  },
  {
    label: 'navigation.assetTypeManagement',
    route: pageUrls.assetTypes,
    icon: 'category_fill',
    divider: true,
    footer: false,
    requiredPermission: { subject: 'asset-types', method: 'GET' },
  },
  {
    label: 'navigation.deviceManagement',
    route: pageUrls.devices,
    icon: 'devices_fill',
    divider: true,
    footer: false,
    requiredPermission: { subject: 'devices', method: 'GET' },
  },
  {
    label: 'navigation.locationManagement',
    route: pageUrls.locations,
    icon: 'location_on_fill',
    divider: true,
    footer: false,
    requiredPermission: { subject: 'locations', method: 'GET' },
  },
  {
    label: 'navigation.userManagement',
    route: pageUrls.users,
    icon: 'manage_accounts_fill',
    divider: true,
    footer: false,
    requiredPermission: { subject: 'users', method: 'GET' },
  },
  {
    label: 'navigation.exportHistory',
    route: pageUrls.exportHistory,
    icon: 'download',
    divider: true,
    footer: false,
    requiredPermission: { subject: 'exports', method: 'GET' },
  },
  {
    label: 'navigation.importHistory',
    route: pageUrls.importHistory,
    icon: 'upload_file',
    divider: true,
    footer: false,
    requiredPermission: { subject: 'imports', method: 'GET' },
  },
  {
    label: 'navigation.sftpExports',
    route: pageUrls.sftpExports,
    icon: 'cloud_upload',
    divider: true,
    footer: false,
    requiredPermission: { subject: 'scan-history-export-profiles', method: 'GET' },
  },

  // Footer items
  {
    label: 'navigation.rolePermissionManagement',
    route: pageUrls.rolePermissions,
    icon: 'admin_panel_settings_fill',
    divider: false,
    footer: true,
    requiredPermission: { subject: 'roles', method: 'GET' },
  },
  {
    label: 'navigation.settings',
    route: pageUrls.settings,
    icon: 'settings_fill',
    divider: true,
    footer: true,
    requiredPermission: { subject: 'settings', method: 'PATCH' },
  },
  {
    label: 'navigation.logViewer',
    route: pageUrls.logs,
    icon: 'terminal_fill',
    divider: true,
    footer: true,
    requiredPermission: { subject: 'logs', method: 'GET' },
  },
];

/**
 * Represents the sidebar component of the application.
 */
@UntilDestroy()
@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  imports: [RouterModule, SynergyComponentsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent implements OnInit {
  private router = inject(Router);
  private permissionService = inject(RbacService);
  translateService = inject(LocalizationService);

  readonly synSideNav = viewChild.required<SynSideNavComponent>('sideNav');
  open = input<boolean>(true);
  readonly onShow = output<void>();
  readonly onHide = output<void>();

  protected readonly pageUrls = pageUrls;

  protected readonly footerNavItems = computed(() =>
    NAVIGATION_CONFIG.filter((item) => item.footer && this.isNavItemVisible(item)),
  );

  protected readonly mainNavItems = computed(() =>
    NAVIGATION_CONFIG.filter((item) => !item.footer && this.isNavItemVisible(item)),
  );

  constructor() {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      const synSideNav = this.synSideNav();
      if (window.innerWidth < 1024 && synSideNav?.nativeElement?.hide) {
        synSideNav.nativeElement.hide();
      }
    });
  }

  ngOnInit(): void {}

  get nativeElement() {
    return this.synSideNav().nativeElement;
  }

  show() {
    const el: any = this.synSideNav()?.nativeElement;
    if (el?.show) el.show();
  }

  hide() {
    const el: any = this.synSideNav()?.nativeElement;
    if (el?.hide) el.hide();
  }

  private isNavItemVisible(item: NavItem): boolean {
    if (!item.requiredPermission) return true;
    return this.permissionService.can(item.requiredPermission.subject, item.requiredPermission.method);
  }
}
